<template lang="">
  <div class="app-create-new-ai-content__section--select-golden-source app-create-new-ai-content__section">
    <div class="app-create-new-ai-content__section-header">
      <h4 class="app-create-new-ai-content__section-title">Select your golden source (optional)</h4>
      <p class="app-create-new-ai-content__section-instructions">{{ instructions }}</p>
      <div v-if="(!hasAiRecommendations || awaitingRecommendations) && !isVideoScript" class="element-toast-notification is--banner">
        <div class="element-featured-icon is--sm is--ai">
          <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
        </div>
        <div class="element-toast-notification__body">
          <h3 class="element-toast-notification__title">AI Topic Recommendation</h3>
          <p>Select the Recommend button to allow AI to recommend domains and topics for the course.</p>
        </div>

        <div class="element-toast-notification__footer">
          <app-button theme="text" @click="getAiSuggestions()" :disabled="awaitingRecommendations">
            {{ awaitingRecommendations ? 'Generating, please wait...' : 'Recommend'}}
          </app-button>
        </div>
      </div>
      <app-error
        v-if="hasErrors"
        class="is--inline"
        v-bind="{ errors }" />
    </div>
    <div class="app-project-sources-golden-source-selection">
      <div class="app-project-sources-golden-source-selection__list is--all">
        <h5 class="app-project-sources-golden-source-selection__list-title">All Options</h5>
        <details v-for="(topic, key) in filteredTopics"
          :key="key"
          :ref="`domain-${topic.uuid}`"
          class="app-project-sources-golden-source-selection__domain app-accordion is--functional">
          <summary class="app-accordion__header">
            <div class="checkbox">
              <label>
                <input
                  type="checkbox"
                  :checked="topic.topics.every(subTopic => selectedSubTopics.includes(subTopic.uuid))"
                  @change="toggleAllSubTopics(key, topic.topics, $event)"
                >
                {{ topic.name }}
              </label>
            </div>
            <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
          </summary>
          <div class="app-accordion__body">
            <div v-for="(subTopic, subKey) in topic.topics" :key="subKey" class="app-project-sources-golden-source-selection__topic">
              <div class="checkbox">
                <label>
                  <input
                    type="checkbox"
                    :checked="selectedSubTopics.includes(subTopic.uuid)"
                    @change="toggleSubTopic(key, subTopic.uuid, $event)"
                  >
                  <div :ref="`subtopic-${subTopic.uuid}`">
                    {{ subTopic.name }}
                    <span v-if="subTopic.metadata?.summary" class="checkbox__supporting-text">
                      {{ subTopic.metadata?.summary }}
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </details>
      </div>
      <div v-if="!isVideoScript" class="app-project-sources-golden-source-selection__list is--selected">
        <h5 class="app-project-sources-golden-source-selection__list-title">
          Your Selections
          <div data-bs-toggle="dropdown" class="element-chip is--ai">
              <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
              AI
          </div>
          <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
              <h4 class="app-tooltip__title">AI Generated</h4>
              <div class="app-tooltip__body">
              <p>We’ve used AI to recommend topics based on the information provided, as noted by this icon.</p>
              </div>
          </div>
        </h5>
        <div v-for="(subTopicUUID, index) in sortedSelectedSubTopics" :key="index" class="app-project-sources-golden-source-selection__list-item">
          <div class="app-project-sources-golden-source-selection__list-item-ai">
            <div v-if="findSubTopicInAiSuggestions(subTopicUUID)" class="element-featured-icon is--xs">
              <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
            </div>
          </div>
          <div class="app-project-sources-golden-source-selection__list-item-text">
            <div class="app-project-sources-golden-source-selection__list-item-domain">{{ parentTopic(subTopicUUID).name}}</div>
            <div class="app-project-sources-golden-source-selection__list-item-name element-button--text element-button"
              @click="openTopicDetails(subTopicUUID)">
              {{ subTopicName(subTopicUUID) }}
            </div>
          </div>
          <button class="app-project-sources-golden-source-selection__list-item-remove element-button--icon element-button" @click="toggleSubTopic(null, subTopicUUID, $event)">
            <svg class="element-icon--close element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-x"></use></svg>
          </button>
        </div>
      </div>
    </div>
    <div class="app-create-new-ai-content__section-footer">
      <app-button theme="secondary" @click="$emit('previousScreen')">
        Back
      </app-button>
      <app-button theme="primary" @click="saveData('review')">
        Next: Final Details
      </app-button>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'

export default {
  name: 'ProjectSourcesSelection',
  props: {
    topics: {
      type: Array,
      default: () => ([])
    },
    selectedTopics: {
      type: Array,
      default: () => ([])
    },
    saveTrigger: {
      type: Boolean,
      required: false,
      default: false
    },
    aiSuggestions: {
      type: Array,
      required: false,
      default: () => ([])
    },
    hasErrors: {
      type: Boolean,
      required: false,
      default: false
    },
    errors: {
      type: Object,
      required: false,
      default: () => ({})
    },
    errorMessage: {
      type: String,
      required: false,
      default: null
    },
    instructions: {
      type: String,
      required: false,
      default: 'Select which Golden Source domains and/or topics you\'d like the course to include. If none are selected, all that are listed will be used.'
    },
    isVideoScript: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      selectedSubTopics: [],
      filteredTopics: [],
      hasAiRecommendations: false,
      awaitingRecommendations: false,
    }
  },
  mounted() {
    this.filteredTopics = this.topics.filter(domain => {
      if(domain.topics.length > 0 ) return true
    })
    this.selectedSubTopics = this.selectedTopics || []
  },
   computed: {
    sortedSelectedSubTopics() {
      const orderedUUIDs = []
      for (const domain of this.filteredTopics) {
        for (const subTopic of domain.topics) {
          orderedUUIDs.push(subTopic.uuid)
        }
      }
      
      return this.selectedSubTopics.sort((a, b) => {
        return orderedUUIDs.indexOf(a) - orderedUUIDs.indexOf(b)
      })
    },
    subTopicMap() {
      const map = new Map()
      this.filteredTopics.forEach(domain => {
        domain.topics.forEach(subTopic => {
          map.set(subTopic.uuid, { domain, subTopic })
        })
      })
      return map
    }
  },
  methods: {
    toggleAllSubTopics(key, subTopics, event) {
      const isChecked = event.target.checked
      const subTopicUUIDs = subTopics.map(subTopic => subTopic.uuid)

      if (isChecked) {
        this.selectedSubTopics = Array.from(new Set([...this.selectedSubTopics, ...subTopicUUIDs]))
      } else {
        this.selectedSubTopics = this.selectedSubTopics.filter(uuid => !subTopicUUIDs.includes(uuid))
      }
      this.$emit('updateSelectedTopics', this.selectedSubTopics)
    }, 
    toggleSubTopic(key, subTopicUUID, event) {
      const isChecked = event.target.checked
      if (isChecked) {
        if (!this.selectedSubTopics.includes(subTopicUUID)) {
          this.selectedSubTopics.push(subTopicUUID)
        }
      } else {
        this.selectedSubTopics = this.selectedSubTopics.filter(uuid => uuid !== subTopicUUID)
      }
      this.$emit('updateSelectedTopics', this.selectedSubTopics)
    },
    saveData (screen, menuSwap = null) {
      this.$emit('saveData', {subScreen: screen, menuSwap})
    },
    findSubTopicInAiSuggestions(subTopicUUID) {
      return this.aiSuggestions.includes(subTopicUUID)
    },
    parentTopic (childTopicUUID) {
      return this.subTopicMap.get(childTopicUUID)?.domain || { name: '' }
    },
    subTopicName (childTopicUUID) {
      for (const domain of this.filteredTopics) {
        for (const subTopic of domain.topics) {
          if (subTopic.uuid === childTopicUUID) {
            return subTopic.name
          }
        }
      }
      return ''
    },
    getAiSuggestions() {
      this.$emit('getAISuggestions')
      this.hasAiRecommendations = true
      this.awaitingRecommendations = true
    },
    openTopicDetails(subTopicUUID) {
      const parentTopic = this.parentTopic(subTopicUUID)
      const accordionRef = this.$refs[`domain-${parentTopic.uuid}`]
      if (accordionRef && accordionRef[0]) {
        accordionRef[0].open = true
        this.$nextTick(() => {
          const subTopicRef = this.$refs[`subtopic-${subTopicUUID}`]
          subTopicRef[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      }
    }
  },
  watch: {
    aiSuggestions(newVal) {
      this.selectedSubTopics = Array.from(new Set([...this.selectedSubTopics, ...(newVal || [])]))
      if(this.awaitingRecommendations)
      {
        this.hasAiRecommendations = true 
        this.awaitingRecommendations = false
      }
    },
    saveTrigger(newVal) {
      if (newVal) {
        this.saveData('review','menuSwap')
      }
    },
    hasErrors(newVal) {
      if(newVal) {
        this.awaitingRecommendations = false
        this.hasAiRecommendations = false
      }
    }
  }
}
</script>
